import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import { Button } from '@mui/material';
import { buildClassName } from 'utils/build-class-name';

//#region Types
export type IconButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost';
export type IconButtonSize = 'small' | 'medium' | 'large';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  /** Button label text */
  label?: string | ReactNode;
  /** Icon component to display on the left */
  leftIcon?: ReactNode;
  /** Icon component to display on the right */
  rightIcon?: ReactNode;
  /** Visual variant */
  variant?: IconButtonVariant;
  /** Size variant */
  size?: IconButtonSize;
  /** Whether the button is in an active/highlighted state */
  isActive?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}
//#endregion

/**
 * IconButton - A button with configurable icon slots
 * 
 * Atomic Level: Molecule
 * 
 * Features:
 * - Left and right icon slots
 * - Multiple variants: primary, secondary, tertiary, ghost
 * - Multiple sizes: small, medium, large
 * - Active state styling
 * - Full accessibility support
 * - Built on Material UI Button for enhanced features
 * 
 * @example
 * ```tsx
 * <IconButton
 *   label="Filter"
 *   leftIcon={<FilterIcon />}
 *   variant="tertiary"
 *   onClick={handleClick}
 * />
 * ```
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  label,
  leftIcon,
  rightIcon,
  variant = 'primary',
  size = 'medium',
  isActive = false,
  className,
  testId,
  type = 'button',
  ...buttonProps
}, ref) => {
  // Map custom variants to Material UI variants
  const getMuiVariant = () => {
    switch (variant) {
      case 'primary':
        return 'contained';
      case 'secondary':
      case 'tertiary':
        return 'outlined';
      case 'ghost':
        return 'text';
      default:
        return 'contained';
    }
  };

  const getMuiColor = () => {
    return variant === 'primary' ? 'primary' : 'inherit';
  };

  // Map custom sizes to Material UI sizes and heights
  const sizeMap = {
    small: { muiSize: 'small' as const, minHeight: '32px', fontSize: '12px', iconSize: '14px' },
    medium: { muiSize: 'medium' as const, minHeight: '40px', fontSize: '14px', iconSize: '15px' },
    large: { muiSize: 'large' as const, minHeight: '50px', fontSize: '16px', iconSize: '18px' },
  };

  const { muiSize, minHeight, fontSize, iconSize } = sizeMap[size];

  return (
    <Button
      ref={ref}
      type={type}
      variant={getMuiVariant()}
      color={getMuiColor()}
      size={muiSize}
      startIcon={leftIcon}
      endIcon={rightIcon}
      className={buildClassName(className)}
      data-testid={testId}
      sx={{
        minHeight,
        borderRadius: '100px', // Pill shape
        textTransform: 'none',
        fontFamily: 'Manrope, sans-serif',
        fontSize,
        transition: 'all 0.2s ease',
        gap: '0.5em',
        whiteSpace: 'nowrap',
        padding: '10px 14px',
        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, .01), 0 4px 8px 0 rgba(0, 0, 0, .02), 0 1px 12px 0 rgba(0, 0, 0, .12)',
        // Active state styling
        ...(isActive && {
          backgroundColor: variant === 'primary' 
            ? 'var(--themes-colors-primary-active, white)'
            : 'var(--themes-colors-active-background, action.selected)',
          borderColor: variant === 'primary' 
            ? '#F3F3F4' 
            : 'var(--themes-colors-primary, )',
          ...(variant === 'tertiary' && {
            color: 'var(--themes-colors-primary, primary.main)',
          }),
        }),
        // Hover state customization to match original
        '&:hover': {
          backgroundColor: variant === 'primary'
            ? 'black'
            : 'white',
          color:  variant === 'primary' ? 'white' : 'black',
          ...(variant !== 'ghost' && variant !== 'primary' && {
            borderColor: 'var(--themes-colors-border-hover, divider)',
          }),
        },
        // Focus visible state
        '&:focus-visible': {
          outline: '2px solid var(--themes-colors-primary, black)',
          outlineOffset: '2px',
        },
        // Disabled state
        '&:disabled': {
          opacity: 0.5,
          cursor: 'not-allowed',
        },
        // Theme variable integration for colors
        ...(variant === 'primary' && {
          backgroundColor: 'white',
          color: 'var(--themes-colors-text, black)',
        }),
        ...(variant !== 'primary' && variant !== 'ghost' && {
          color: 'var(--themes-colors-text, rgba(0, 0, 0, .7))',
          borderColor: 'var(--themes-colors-border, divider)',
        }),
        ...(variant === 'ghost' && {
          color: 'var(--themes-colors-text, rgba(0, 0, 0, .7))',
        }),
        '& .MuiButton-startIcon': {
          marginRight: '0',
          marginLeft: '0',
          '& > *': {
            fontSize: iconSize,
          },
        },
        '& .MuiButton-endIcon': {
          marginRight: '0',
          marginLeft: '0',
          '& > *': {
            fontSize: iconSize,
          },
        },
      }}
      {...buttonProps}
    >
      {label}
    </Button>
  );
});

IconButton.displayName = 'IconButton';

export default IconButton;
